import React, { useState } from 'react';
import {
  Box, Button, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Alert, Paper, Menu, MenuItem, CircularProgress,
} from '@mui/material';

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function totalSize(b) {
  return formatBytes((b.files_size_bytes || 0) + (b.db_size_bytes || 0));
}

const TYPE_LABEL = {
  scheduled: 'Scheduled',
  manual: 'Manual',
  pre_restore: 'Pre-restore',
};

function StatusChip({ status }) {
  if (status === 'succeeded') return <Chip size="small" color="success" label="Succeeded" />;
  if (status === 'failed') return <Chip size="small" color="error" label="Failed" />;
  if (status === 'running') return <Chip size="small" color="info" label="Running" />;
  return <Chip size="small" label={status} />;
}

function DownloadMenu({ backup, onDownload }) {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);
  const handleOpen = (e) => setAnchor(e.currentTarget);
  const handleClose = () => setAnchor(null);
  const pick = (part) => {
    handleClose();
    onDownload(backup, part);
  };
  return (
    <>
      <Button size="small" onClick={handleOpen}>Download</Button>
      <Menu anchorEl={anchor} open={open} onClose={handleClose}>
        <MenuItem onClick={() => pick('files')}>Files (.tar.gz)</MenuItem>
        <MenuItem onClick={() => pick('db')}>Database (.sql.gz)</MenuItem>
      </Menu>
    </>
  );
}

export default function BackupList({ backups, onRestore, onRunNow, onDownload, enabled }) {
  if (!enabled) {
    return (
      <Alert severity="info">
        Backups are only available for sites hosted on the nerv server.
        Set <strong>host_provider</strong> to <code>nerv</code> in the Edit dialog to enable.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Backups</Typography>
        <Button variant="contained" onClick={onRunNow}>Run backup now</Button>
      </Box>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date / time</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {backups === null || backups === undefined ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, color: 'text.secondary' }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">Loading backups…</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : backups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}><em>No backups yet.</em></TableCell>
              </TableRow>
            ) : (
              backups.map((b) => (
                <TableRow key={b.id}>
                  <TableCell>{b.started_at}</TableCell>
                  <TableCell>{TYPE_LABEL[b.type] || b.type}</TableCell>
                  <TableCell>{totalSize(b)}</TableCell>
                  <TableCell><StatusChip status={b.status} /></TableCell>
                  <TableCell align="right">
                    {b.status === 'succeeded' && (
                      <>
                        {onDownload && <DownloadMenu backup={b} onDownload={onDownload} />}
                        <Button size="small" color="warning" onClick={() => onRestore(b)}>
                          Restore
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
